// 마을 퀘스트 카드의 "다음 목표" 칸 모델(순수 로직). growthModel이 만든 목표를 카드 위계(그림·이름·수치·아래 줄)로 나눈다.
// 정보는 goalLines와 같다: 이름+수치 = 첫 줄 내용, detail = 둘째 줄 그대로. 문구·가격·판정을 새로 만들지 않는다.
import { REGIONS } from '../shared/regions';
import { VILLAGES } from '../shared/levels';
import { REBIRTH_GATE } from '../shared/rebirth';
import type { RegionId } from '../shared/levels';
import type { NpcRole } from '../shared/rpg';
import { deltaText, goalItemName, goalLines, type GrowthGoal } from './growthModel';
import { tierTone, type TierTone } from './regionModel';

/** 목표 칸의 「자동」 버튼이 하는 일: NPC에게 걸어가 대화 · 문으로 걸어가 입장 · 가방 열기 */
/** 마을 워프 칸(D3a, PR #160 리뷰): 도착했던 마을 ①~④. 지금 마을은 누를 수 없다. 마을이 하나뿐이면 빈 목록(칸을 숨긴다) */
export interface WarpChip { readonly town: number; readonly label: string; readonly name: string; readonly current: boolean }
export function warpChips(town: number, highest: number, names: readonly string[]): WarpChip[] {
  const out: WarpChip[] = [];
  const top = Math.max(1, Math.min(names.length, Math.floor(highest) || 1));
  if (top < 2) return out;
  for (let t = 1; t <= top; t++) out.push({ town: t, label: '①②③④⑤⑥'[t - 1] ?? String(t), name: names[t - 1], current: t === town });
  return out;
}

export type QuestAction = { readonly kind: 'npc'; readonly role: NpcRole } | { readonly kind: 'door'; readonly region: RegionId } | { readonly kind: 'bag' } | { readonly kind: 'gate' };

export interface QuestGoalView {
  /** item: 장비 그림, gate: 지역 문 그림 */
  readonly icon: 'item' | 'gate';
  /** 장비 아이콘 그림 경로(public/assets 기준, 장비 목표만. 아니면 '') */
  readonly itemIcon: string;
  readonly tone: TierTone;
  /** 장비·지역 이름(단일 능력치면 능력치 이름까지) */
  readonly name: string;
  /** 큰 숫자 줄(예: 2,000 → 2,200). 없으면 '' */
  readonly change: string;
  /** 아래 줄(가격·보유 골드·판매 NPC 위치, 장착 안내, 잠금 사유, 기록 조건) */
  readonly detail: string;
  /** 자동 진행 동작. 할 일이 없으면 null */
  readonly action: QuestAction | null;
  /** 「추천 장비 모두 장착」으로 한 번에 낄 수 있는 장비 수(장착 목표만, 아니면 0) */
  readonly equipCount: number;
}

const HEAD = '다음 목표 · ';

export function questGoalView(g: GrowthGoal, touch: boolean): QuestGoalView | null {
  const lines = goalLines(g, touch);
  if (!lines) return null;
  const head = lines[0].startsWith(HEAD) ? lines[0].slice(HEAD.length) : lines[0];
  if (g.kind === 'record' || !g.item) {
    const info = g.region ? REGIONS[g.region] : null;
    return { icon: 'gate', itemIcon: '', tone: info ? tierTone(info) : 'easy', name: head, change: '', detail: lines[1], action: g.region ? { kind: 'door', region: g.region } : null, equipCount: 0 };
  }
  const d = g.deltas;
  const one = d.length === 1;
  const action: QuestAction | null = g.kind === 'equip' ? { kind: 'bag' } : g.seller ? { kind: 'npc', role: g.seller.role } : null;
  return {
    icon: 'item', itemIcon: g.item.icon, tone: 'easy',
    // 장비 이름 + 할 일(장착/구매) · 능력치: 합쳐 읽으면 growthModel.goalLines 첫 줄(「다음 목표 · 」 뒤)과 같다
    name: goalItemName(g) + (one ? ' · ' + d[0].label : ' ·'),
    change: one ? d[0].from + ' → ' + d[0].to : deltaText(d),
    detail: lines[1],
    action,
    equipCount: g.kind === 'equip' ? g.upgrades.length : 0,
  };
}


/**
 * 환생 자격이 생긴 순간(Lv99 + 윤회의 문 퀘스트 + 다음 모드 공개 = rebirthDenial null)의 추적기 목표(L1029 O3).
 * 새 팝업 없이 기존 추적기 줄을 쓰고, 「자동」은 지금 마을에서 마을①까지 워프한 뒤 윤회의 문 앞까지 걷는다(autoPilot gate).
 */
export function rebirthGoalView(): QuestGoalView {
  const town = VILLAGES[REBIRTH_GATE.town - 1]?.name ?? '마을①'; // 짧은 이름 줄(폰 추적기 폭)에는 문 이름만, 장소는 아래 줄
  return {
    icon: 'gate', itemIcon: '', tone: 'hard',
    name: '윤회의 문',
    change: '', detail: town + '(①) 광장 · 문 앞에서 환생해요',
    action: { kind: 'gate' }, equipCount: 0,
  };
}

/** 추적기에 윤회의 문 목표를 띄울 때: 환생 거절 사유가 없다(Lv99·퀘스트·모드 공개). 환생 뒤(레벨 1)나 자격 전에는 false */
export function rebirthGoalOn(rb: { readonly denial: string | null } | undefined): boolean { return !!rb && rb.denial === null; }
